import { apiGet, apiPatch } from "@/services/http";
import { resolveProfilePicUrl } from "@/lib/media";
import { endpoints } from "./endpoints";
import type { ApiEnvelope, AuthProfile, ProfileUpdatePayload } from "@/types/api";
import { ApiError } from "@/services/http/errors";

const PROFILE_PIC_MAX_BYTES = 2 * 1024 * 1024;
const PROFILE_PIC_TYPES = new Set(["image/jpeg", "image/png", "image/gif"]);

function withResolvedPic(profile: AuthProfile): AuthProfile {
  return {
    ...profile,
    profile_pic: resolveProfilePicUrl(profile.profile_pic),
  };
}

export async function fetchProfile() {
  const response = await apiGet<ApiEnvelope<AuthProfile>>(endpoints.profile);

  if (!response?.success || !response.data) {
    throw new ApiError(0, response, response?.message || "Could not load profile.");
  }

  return withResolvedPic(response.data);
}

export async function updateProfile(payload: ProfileUpdatePayload) {
  const response = await apiPatch<ApiEnvelope<AuthProfile>, ProfileUpdatePayload>(
    endpoints.profile,
    payload,
  );

  if (!response?.success || !response.data) {
    throw new ApiError(0, response, response?.message || "Could not update profile.");
  }

  return withResolvedPic(response.data);
}

export function validateProfilePicFile(file: File): string | null {
  if (!PROFILE_PIC_TYPES.has(file.type)) {
    return "Only JPEG, PNG, and GIF images are allowed.";
  }
  if (file.size > PROFILE_PIC_MAX_BYTES) {
    return "Image must be smaller than 2 MB.";
  }
  return null;
}

export async function updateProfilePic(file: File) {
  const validationError = validateProfilePicFile(file);
  if (validationError) {
    throw new ApiError(400, null, validationError);
  }

  const formData = new FormData();
  formData.append("profile_pic", file);

  const response = await apiPatch<ApiEnvelope<AuthProfile>, FormData>(
    endpoints.profile,
    formData,
  );

  if (!response?.success || !response.data) {
    throw new ApiError(0, response, response?.message || "Could not update profile picture.");
  }

  return withResolvedPic(response.data);
}
